/**
 * IndexedDB Service for offline data storage
 * Stores events, expenses, budgets, and a sync queue
 */

const DB_NAME = 'travel-itinerary-offline';
const DB_VERSION = 1;

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[IndexedDB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[IndexedDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.log('[IndexedDB] Upgrading database...');

      // Events store
      if (!database.objectStoreNames.contains('events')) {
        const eventsStore = database.createObjectStore('events', { keyPath: 'id' });
        eventsStore.createIndex('user_id', 'user_id', { unique: false });
        eventsStore.createIndex('start_datetime', 'start_datetime', { unique: false });
      }

      // Expenses store
      if (!database.objectStoreNames.contains('expenses')) {
        const expensesStore = database.createObjectStore('expenses', { keyPath: 'id' });
        expensesStore.createIndex('user_id', 'user_id', { unique: false });
        expensesStore.createIndex('event_id', 'event_id', { unique: false });
      }

      // Budgets store
      if (!database.objectStoreNames.contains('budgets')) {
        const budgetsStore = database.createObjectStore('budgets', { keyPath: 'id' });
        budgetsStore.createIndex('user_id', 'user_id', { unique: false });
      }

      // User store (for preferences)
      if (!database.objectStoreNames.contains('user')) {
        database.createObjectStore('user', { keyPath: 'id' });
      }

      // Sync queue - stores pending changes to sync when online
      if (!database.objectStoreNames.contains('syncQueue')) {
        const syncStore = database.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('type', 'type', { unique: false });
      }

      // Metadata store - tracks last sync time, etc.
      if (!database.objectStoreNames.contains('metadata')) {
        database.createObjectStore('metadata', { keyPath: 'key' });
      }

      console.log('[IndexedDB] Database upgrade complete');
    };
  });
}

/**
 * Get a transaction and object store
 */
function getStore(storeName, mode = 'readonly') {
  if (!db) throw new Error('Database not initialized');
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
}

/**
 * Generic CRUD operations
 */
export async function getAll(storeName) {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = getStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getById(storeName, id) {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = getStore(storeName);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function put(storeName, data) {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = getStore(storeName, 'readwrite');
    const request = store.put(data);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function putMany(storeName, items) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    items.forEach(item => store.put(item));
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function deleteById(storeName, id) {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = getStore(storeName, 'readwrite');
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete all items matching an index value
 * Used for cascade deletes (e.g., delete expenses when event is deleted)
 */
export async function deleteByIndex(storeName, indexName, indexValue) {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = getStore(storeName, 'readwrite');
    const index = store.index(indexName);
    const request = index.openCursor(IDBKeyRange.only(indexValue));
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
}

export async function clearStore(storeName) {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = getStore(storeName, 'readwrite');
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Sync Queue Operations
 */
export async function addToSyncQueue(action) {
  const queueItem = {
    ...action,
    timestamp: Date.now(),
    retryCount: 0,
  };
  return put('syncQueue', queueItem);
}

export async function getSyncQueue() {
  const items = await getAll('syncQueue');
  // Sort by timestamp (oldest first)
  return items.sort((a, b) => a.timestamp - b.timestamp);
}

export async function removeFromSyncQueue(id) {
  return deleteById('syncQueue', id);
}

export async function clearSyncQueue() {
  return clearStore('syncQueue');
}

export async function updateSyncQueueItem(item) {
  return put('syncQueue', item);
}

/**
 * Metadata Operations
 */
export async function getMetadata(key) {
  const result = await getById('metadata', key);
  return result?.value;
}

export async function setMetadata(key, value) {
  return put('metadata', { key, value });
}

/**
 * Bulk sync from server
 */
export async function syncFromServer(events, expenses, budgets, user) {
  await initDB();
  
  // Clear existing data and replace with server data
  await clearStore('events');
  await clearStore('expenses');
  await clearStore('budgets');
  
  if (events?.length) await putMany('events', events);
  if (expenses?.length) await putMany('expenses', expenses);
  if (budgets?.length) await putMany('budgets', budgets);
  if (user) await put('user', user);
  
  // Update last sync time
  await setMetadata('lastSync', Date.now());
  
  console.log('[IndexedDB] Synced from server:', {
    events: events?.length || 0,
    expenses: expenses?.length || 0,
    budgets: budgets?.length || 0,
  });
}

/**
 * Get pending sync count
 */
export async function getPendingSyncCount() {
  const queue = await getSyncQueue();
  return queue.length;
}

/**
 * Check if database has any cached data
 */
export async function hasOfflineData() {
  await initDB();
  const events = await getAll('events');
  return events.length > 0;
}
